// Corrections AUTOMATIQUES des erreurs mécaniques que l'IA laisse dans un
// document JRAF (« draw » sur un texte, `dash: "true"`, opacité 1,5…). Ces
// erreurs ont UNE réponse évidente : on la pose sans demander à l'IA (qui la
// refaisait parfois trois réparations de suite) et sans bloquer la création.
// Pur, ne lève jamais : un document illisible revient tel quel. Appliqué AVANT
// la validation, par la génération (serveur) et par l'éditeur JSON (admin).

import { DRAWABLE_TYPES, type JrafObjectType } from './schema/objects';
import { SHOW_EFFECTS } from './schema/steps';

export interface JrafFix {
  /** chemin du champ corrigé (`scenes[3].steps[2].effect`) */
  path: string;
  message: string;
}

export interface AutoFixResult {
  doc: unknown;
  fixes: JrafFix[];
}

type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);

const HIDE_EFFECTS = ['fade', 'none'];
/** Mots qui disent « trait plein » ; tout autre texte (« true », « dashed », « 4 4 ») vaut des pointillés. */
const SOLID_WORDS = ['false', 'non', 'no', 'none', 'solid', 'plein', '0', ''];

/** `"true"`, `"dashed"`, `"4 4"`, 1 → true ; `"false"`, `"none"`, `"solid"`, 0, `""` → false. */
function toBoolean(v: unknown): boolean {
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') return !SOLID_WORDS.includes(v.trim().toLowerCase());
  if (Array.isArray(v)) return v.length > 0;
  return Boolean(v);
}

/** Nombre lisible dans `v` (« 0,5 », « 3px ») ; null sinon. */
function toNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string') return null;
  const n = Number.parseFloat(v.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** Style d'un objet : `dash` en vrai/faux, épaisseur et opacité dans leurs bornes. */
function fixStyle(style: Obj, path: string, fixes: JrafFix[]) {
  if ('dash' in style && typeof style.dash !== 'boolean') {
    const before = JSON.stringify(style.dash);
    style.dash = toBoolean(style.dash);
    fixes.push({ path: `${path}.dash`, message: `dash ${before} → ${String(style.dash)}` });
  }
  const bounds: [string, number, number][] = [
    ['strokeWidth', 0, 20],
    ['opacity', 0, 1],
  ];
  for (const [key, min, max] of bounds) {
    if (!(key in style)) continue;
    const n = toNumber(style[key]);
    if (n === null) continue;
    const clamped = Math.min(max, Math.max(min, n));
    if (clamped !== style[key]) {
      fixes.push({
        path: `${path}.${key}`,
        message: `${key} ${JSON.stringify(style[key])} → ${clamped}`,
      });
      style[key] = clamped;
    }
  }
}

function targetsOf(step: Obj): string[] {
  const t = step.target;
  if (typeof t === 'string') return [t];
  return Array.isArray(t) ? t.filter((x): x is string => typeof x === 'string') : [];
}

/** Corrige les erreurs mécaniques d'un document JRAF (copie ; l'original n'est pas touché). */
export function autoFixJraf(input: unknown): AutoFixResult {
  if (!isObj(input)) return { doc: input, fixes: [] };
  let doc: Obj;
  try {
    doc = structuredClone(input);
  } catch {
    return { doc: input, fixes: [] };
  }
  const fixes: JrafFix[] = [];

  const types = new Map<string, string>();
  const objects = Array.isArray(doc.objects) ? doc.objects : [];
  objects.forEach((o, i) => {
    if (!isObj(o)) return;
    if (typeof o.id === 'string' && typeof o.type === 'string') types.set(o.id, o.type);
    if (isObj(o.style)) fixStyle(o.style, `objects[${i}].style`, fixes);
  });

  const scenes = Array.isArray(doc.scenes) ? doc.scenes : [];
  scenes.forEach((scene, si) => {
    if (!isObj(scene) || !Array.isArray(scene.steps)) return;
    scene.steps.forEach((step, ti) => {
      if (!isObj(step)) return;
      const path = `scenes[${si}].steps[${ti}].effect`;
      if (step.action === 'object.show') {
        const effect = step.effect;
        if (effect !== undefined && !(SHOW_EFFECTS as readonly unknown[]).includes(effect)) {
          step.effect = 'fade';
          fixes.push({ path, message: `effet « ${String(effect)} » inconnu → « fade »` });
        } else if (effect === 'draw') {
          // « draw » trace un contour : un texte, une formule, un point… apparaissent en fondu
          const notDrawable = targetsOf(step)
            .map((t) => types.get(t))
            .filter(
              (t): t is string => t !== undefined && !DRAWABLE_TYPES.includes(t as JrafObjectType),
            );
          if (notDrawable.length) {
            step.effect = 'fade';
            fixes.push({
              path,
              message: `« draw » sur un « ${notDrawable[0]} » → « fade »`,
            });
          }
        }
      } else if (step.action === 'object.hide') {
        const effect = step.effect;
        if (effect !== undefined && !HIDE_EFFECTS.includes(effect as string)) {
          step.effect = effect === 'none' ? 'none' : 'fade';
          fixes.push({ path, message: `effet de disparition « ${String(effect)} » → « fade »` });
        }
      } else if (step.action === 'object.style') {
        const p = `scenes[${si}].steps[${ti}]`;
        if ('dash' in step) {
          // object.style ne change pas les pointillés : le champ est refusé
          delete step.dash;
          fixes.push({ path: `${p}.dash`, message: 'dash retiré (object.style ne le change pas)' });
          // un object.style qui ne changeait que les pointillés : il ne change plus rien de visible
          if (!['fill', 'stroke', 'strokeWidth', 'opacity'].some((k) => k in step))
            step.opacity = 1;
        }
        fixStyle(step, p, fixes);
      }
    });
  });

  return { doc, fixes };
}
